import { GeoModelJobStatus } from "../models/GeoModelJob";

/**
 * Shared status taxonomy for geo calc / sim jobs, used by both useJobStatus
 * (polling) and StatusPill (rendering) so the two stay in sync.
 *
 * - IN_PROGRESS: raw backend strings meaning a run is currently active. Worth
 *   polling and shown with a spinner.
 * - SETTLED: known terminal GeoModelJobStatus values. Won't change without a
 *   new run, so polling stops.
 * - Anything else truthy is UNKNOWN: an unrecognized future status. Treated as
 *   neutral (no spinner, not clickable) and excluded from polling so it never
 *   spins forever.
 *
 * IMPORTANT: the backend delivers live calculation progress *as the status
 * string itself* — the worker writes "Started", then "40/128", "80/128", …
 * (wells processed / total) before a terminal status. Both "Started" and the
 * "n/m" progress form must count as IN_PROGRESS, otherwise the pill treats them
 * as UNKNOWN, stops polling, and freezes mid-run (see isProgressStatus below).
 */
export const IN_PROGRESS_STATUSES: readonly string[] = [
  "Started",
  "Queued",
  "Calculating",
  "Running",
  GeoModelJobStatus.Cancelling
];

// Matches the backend's live-progress status, e.g. "40/128" = 40 of 128 wells
// processed. The worker writes this via onUpdateProgress($"{count}/{max}").
const PROGRESS_STATUS_RE = /^\d+\/\d+$/;

export function isProgressStatus(status?: Maybe<string>): boolean {
  return !!status && PROGRESS_STATUS_RE.test(status);
}

export const SETTLED_STATUSES: readonly string[] = [
  GeoModelJobStatus.Completed,
  GeoModelJobStatus.PartiallyCompleted,
  GeoModelJobStatus.Error,
  GeoModelJobStatus.NotStarted,
  GeoModelJobStatus.Cancelled
];

export function isInProgressStatus(status?: Maybe<string>): boolean {
  return !!status && (IN_PROGRESS_STATUSES.includes(status) || isProgressStatus(status));
}

export function isSettledStatus(status?: Maybe<string>): boolean {
  return !!status && SETTLED_STATUSES.includes(status);
}
